/**
 * Permalinks to a single asset.
 *
 * A link is how one person shows another a specific asset, so it has to survive
 * being pasted into Slack and opened days later by someone whose library is in
 * a different state. Two consequences shape what's here:
 *
 * The link carries the asset id and nothing else. No tab, no filters, no page
 * offset — all of those describe the sender's view of the library rather than
 * the asset, and all of them go stale. A link sent from the Staged tab must
 * still open the asset after someone publishes it, which it can't do if the tab
 * is baked in; the tab is derived from the asset's own status on arrival.
 *
 * The id is validated before it is trusted. It arrives from the address bar, so
 * it can be anything, and an unvalidated value would be handed straight to the
 * API as a path segment.
 */

export const ASSET_PARAM = 'asset';

/**
 * Loose on version and variant, strict on shape. The backend mints v4 ids, but
 * this only has to reject values that aren't ids at all — the API is the thing
 * that decides whether a well-formed id names an asset.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isAssetId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/**
 * The asset a URL points at, or null.
 *
 * @param search A query string, with or without the leading `?`.
 */
export function readAssetIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(ASSET_PARAM);
  return isAssetId(value) ? value : null;
}

/**
 * The URL to copy for an asset.
 *
 * Absolute, because the point is to paste it somewhere that isn't this app.
 * Built from the current location's origin and path so it stays correct across
 * dev, the `.dev.vml-map.ai` host and prod without a configured base URL.
 *
 * Existing query params are dropped rather than preserved: they are the
 * sender's filters, and a recipient opening a link should land on the asset,
 * not inherit a view of the library that has nothing to do with it.
 */
export function buildAssetPermalink(assetId: string, href: string): string {
  const url = new URL(href);
  url.search = '';
  url.hash = '';
  url.searchParams.set(ASSET_PARAM, assetId);
  return url.toString();
}

/**
 * The same URL with the open asset added, changed or removed.
 *
 * Used for the address bar rather than for sharing, so unlike
 * `buildAssetPermalink` this keeps the other params — the tab the user is on is
 * still theirs while they browse, and dropping it here would reset the grid
 * behind the overlay every time one was opened.
 */
export function withOpenAsset(href: string, assetId: string | null): string {
  const url = new URL(href);
  if (assetId === null) {
    url.searchParams.delete(ASSET_PARAM);
  } else {
    url.searchParams.set(ASSET_PARAM, assetId);
  }
  return url.toString();
}
